// Localisation layer for the content in src/data.
//
// The English files stay the single source of truth for structure and identity:
// ids, slugs, tags, colours, asset paths, prices and technology lists live there
// only. The `*.nl.js` files carry nothing but translated prose, keyed by the
// English identity, so adding a locale never risks desyncing the data shape.
//
// Identity that must survive translation:
//   service.slug   -> /contact?service=<slug>
//   tier.name      -> /contact?tier=<name>
//   addon.id       -> /contact?addon=<id> and the quote payload
//   project.tag    -> the Works page filter
// Addons get an explicit `id` here (their English label) because their visible
// label is translated, while the label is what older shared URLs carry.

import { useMemo } from 'react';
import { useLocale } from './LocaleContext';
import { services as baseServices } from '../data/services';
import { projects as baseProjects } from '../data/projects';
import { servicesNl } from '../data/services.nl';
import { projectsNl } from '../data/projects.nl';

const pick = (override, fallback) => (override != null ? override : fallback);

const withAddonIds = (service) => ({
  ...service,
  addons: (service.addons || []).map((addon) => ({
    ...addon,
    id: addon.id != null ? addon.id : addon.label,
  })),
});

export const localizeService = (service, lang) => {
  if (!service) return service;
  const base = withAddonIds(service);
  const nl = lang === 'nl' ? servicesNl[service.id] : null;
  if (!nl) return base;

  return {
    ...base,
    title: pick(nl.title, base.title),
    cost: pick(nl.cost, base.cost),
    outcome: pick(nl.outcome, base.outcome),
    bestIf: pick(nl.bestIf, base.bestIf),
    description: pick(nl.description, base.description),
    longDescription: pick(nl.longDescription, base.longDescription),
    process: pick(nl.process, base.process),
    tiers: (base.tiers || []).map((tier) => {
      const tierNl = nl.tiers?.[tier.name];
      if (!tierNl) return tier;
      return {
        ...tier,
        timeline: pick(tierNl.timeline, tier.timeline),
        bestFor: pick(tierNl.bestFor, tier.bestFor),
        features: pick(tierNl.features, tier.features),
        notIncluded: pick(tierNl.notIncluded, tier.notIncluded),
      };
    }),
    addons: base.addons.map((addon) => {
      const addonNl = nl.addons?.[addon.id];
      if (!addonNl) return addon;
      return {
        ...addon,
        label: pick(addonNl.label, addon.label),
        indicative: pick(addonNl.indicative, addon.indicative),
      };
    }),
  };
};

export const localizeProject = (project, lang) => {
  if (!project) return project;
  const nl = lang === 'nl' ? projectsNl[project.id] : null;
  if (!nl) return project;

  return {
    ...project,
    title: pick(nl.title, project.title),
    description: pick(nl.description, project.description),
    teaser: pick(nl.teaser, project.teaser),
    imageAlt: pick(nl.imageAlt, project.imageAlt),
    sector: pick(nl.sector, project.sector),
    services: pick(nl.services, project.services),
    caseStudy: project.caseStudy
      ? {
          ...project.caseStudy,
          challenge: pick(nl.caseStudy?.challenge, project.caseStudy.challenge),
          approach: pick(nl.caseStudy?.approach, project.caseStudy.approach),
          result: pick(nl.caseStudy?.result, project.caseStudy.result),
          role: pick(nl.caseStudy?.role, project.caseStudy.role),
          deliverables: pick(nl.caseStudy?.deliverables, project.caseStudy.deliverables),
        }
      : project.caseStudy,
    relatedLinks: project.relatedLinks
      ? { ...project.relatedLinks, label: pick(nl.relatedLinks?.label, project.relatedLinks.label) }
      : project.relatedLinks,
  };
};

// Tags stay English in the data because they are filter identities; only their
// display label is translated. Unknown tags fall through to the raw value.
export const localizeTag = (tag, t) => {
  if (!tag) return tag;
  const translated = t(`tags.${tag}`);
  return translated === `tags.${tag}` ? tag : translated;
};

// Project section headings ("WATCH\nTHE VIDEO", "3D MODEL", ...) are authored
// per project in the English data and repeat verbatim across dozens of entries.
// Instead of duplicating them into a NL data file we translate them by a slug of
// the English text; one-off headings simply pass through untranslated.
const slugifyTitle = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const localizeSectionTitle = (value, t) => {
  if (!value) return value;
  const key = `projectPage.sectionTitles.${slugifyTitle(value)}`;
  const translated = t(key);
  return translated === key ? value : translated;
};

// The two enabled client-review blocks carry either a URL or a short paragraph.
// Prose is translated under the slug of its heading; URLs have no key and pass
// through unchanged.
export const localizeSectionBody = (heading, body, t) => {
  if (!body || !heading) return body;
  const key = `projectPage.sectionBodies.${slugifyTitle(heading)}`;
  const translated = t(key);
  return translated === key ? body : translated;
};

export const useLocalizedServices = () => {
  const { lang } = useLocale();
  return useMemo(() => baseServices.map((service) => localizeService(service, lang)), [lang]);
};

export const useLocalizedProjects = () => {
  const { lang } = useLocale();
  return useMemo(() => baseProjects.map((project) => localizeProject(project, lang)), [lang]);
};

// For call sites that resolve content dynamically (lookup by slug or id).
export const useLocalizer = () => {
  const { lang } = useLocale();
  return useMemo(
    () => ({
      service: (service) => localizeService(service, lang),
      project: (project) => localizeProject(project, lang),
      projects: (list = []) => list.map((project) => localizeProject(project, lang)),
    }),
    [lang]
  );
};
